/* eslint-disable react/prop-types */
/* eslint-disable react/destructuring-assignment */
import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Button,
  Alert,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  AttachMoney as AttachMoneyIcon,
  AppRegistration as AppRegistrationIcon,
  ExitToApp as ExitToAppIcon,
} from '@mui/icons-material';
import { useAuth } from '../../utils/authContext';

const drawerWidth = 240;

function AdminLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [layoutError, setLayoutError] = useState(null);
  const { logout, isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    console.log('AdminLayout: Composant chargé, path actuel:', location.pathname);
    console.log("AdminLayout: État d'authentification:", { isLoggedIn });

    // Vérifier si l'utilisateur est connecté
    if (!isLoggedIn) {
      console.log('AdminLayout: Utilisateur non connecté, redirection vers login');
      navigate('/admin/login', { replace: true });
    }
  }, [isLoggedIn, navigate, location]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path) => {
    console.log('AdminLayout: Navigation vers', path);
    navigate(`/admin/${path}`);
    setMobileOpen(false);
  };

  const handleLogout = () => {
    console.log('AdminLayout: Déconnexion');
    logout();
    navigate('/admin/login', { replace: true });
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: 'dashboard' },
    { text: 'Tenants', icon: <BusinessIcon />, path: 'tenants' },
    { text: 'Plans', icon: <AttachMoneyIcon />, path: 'plans' },
    { text: 'Fonctionnalités', icon: <AppRegistrationIcon />, path: 'features' },
  ];

  const drawer = (
    <div>
      <Toolbar sx={{ justifyContent: 'center' }}>
        <Typography variant="h6" component="div">
          Admin Drivesoft
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              onClick={() => handleNavigation(item.path)}
              selected={location.pathname === item.path}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout}>
            <ListItemIcon><ExitToAppIcon /></ListItemIcon>
            <ListItemText primary="Déconnexion" />
          </ListItemButton>
        </ListItem>
      </List>
    </div>
  );

  console.log('AdminLayout: Rendu du layout admin');

  // Gestionnaire d'erreurs pour l'Outlet
  const handleOutletError = (error) => {
    console.error('AdminLayout: Erreur dans l\'Outlet', error);
    setLayoutError(error.message || 'Erreur lors du rendu du contenu');
  };

  return (
    <Box sx={{ display: 'flex' }} className="admin-layout">
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Drivesoft Administration
          </Typography>
          <Button color="inherit" onClick={handleLogout}>
            Déconnexion
          </Button>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          marginTop: '64px',
          minHeight: 'calc(100vh - 64px)', // Assurer que le contenu prend tout l'espace vertical
        }}
      >
        {layoutError ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {layoutError}
          </Alert>
        ) : (
          <ErrorBoundary onError={handleOutletError}>
            <Outlet />
          </ErrorBoundary>
        )}
      </Box>
    </Box>
  );
}

// Composant pour gérer les erreurs dans les composants enfants
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught in boundary:', error, errorInfo);
    if (this.props.onError) {
      this.props.onError(error);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="error" gutterBottom>
            Une erreur s'est produite lors du chargement du contenu.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {this.state.error?.message || 'Erreur inconnue'}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            sx={{ mt: 2 }}
            onClick={() => this.setState({ hasError: false, error: null })}
          >
            Réessayer
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default AdminLayout;
